/**
 * What ShopStag granted this site (backend: shop/logic/entitlements.py). Public /site-config
 * carries `site_mode` ('site' = website only, 'shop' = full shop) and `modules`. A payload without
 * them (older backend, Studio) means the full shop as before plans — nothing may disappear by
 * accident, and nothing new appears either.
 * Pure module — shared by proxy.ts, server and client code.
 */
export type ModuleKey =
  | 'shop'
  | 'allegro'
  | 'marketplaces'
  | 'warehouse'
  | 'tickets'
  | 'logistics'
  | 'games'
  | 'school_pack'
  | 'wcag_toolbar'
  | 'live_sale'
  | 'stays'
  // Cloud / Enterprise features (roadmap C/D) — only ever on by an explicit grant (GRANT_ONLY_MODULES).
  | 'invoicing'
  | 'accounting_export'
  | 'pos'
  | 'b2b'
  | 'erp';

type EntitlementSource = { site_mode?: unknown; modules?: unknown } | null | undefined;

export function isSiteMode(config: EntitlementSource): boolean {
  return config?.site_mode === 'site';
}

/** Opt-in extras (accessibility toolbar, school pack) need an explicit grant — the "no plan yet =
 *  everything" default must not switch them on for every existing shop. */
export function hasGrantedModule(config: (EntitlementSource & { modules_configured?: unknown }) | null | undefined, module: ModuleKey): boolean {
  return config?.modules_configured === true && Array.isArray(config.modules) && (config.modules as unknown[]).includes(module);
}

/** Backend GRANT_ONLY_MODULES: features that came with the plans. A payload without `modules`
 *  (older backend, Studio, config still loading) must not switch them on. */
const GRANT_ONLY_MODULES = new Set<ModuleKey>(['warehouse', 'invoicing', 'accounting_export', 'pos', 'b2b', 'erp']);

export function hasModule(config: EntitlementSource, module: ModuleKey): boolean {
  if (!config || !Array.isArray(config.modules)) return !GRANT_ONLY_MODULES.has(module);
  return (config.modules as unknown[]).includes(module);
}

/** Storefront routes (internal Polish names) that only exist in a shop. /moje-konto: D5 — hidden. */
export const SHOP_STOREFRONT_ROUTES = ['/koszyk', '/zamowienie', '/produkty', '/kategoria', '/oferta', '/moje-konto', '/rejestracja', '/noclegi', '/rezerwacja'];

/** Admin sections a website without the shop module has no use for (their APIs answer 403). */
export const SHOP_ADMIN_ROUTES = [
  '/admin/orders', '/admin/negotiations', '/admin/products', '/admin/categories', '/admin/brands',
  '/admin/attributes', '/admin/catalog-filters', '/admin/marketplace', '/admin/allegro', '/admin/groups',
  '/admin/oferty', '/admin/reviews', '/admin/dostawy', '/admin/checkin', '/admin/venues', '/admin/events',
  '/admin/logistyka', '/admin/spec-templates', '/admin/stripe-connect', '/admin/live', '/admin/noclegi',
];

/** /admin/customers is not listed: panel users (customers?role=admin) live there too. */
export function isShopAdminPath(pathname: string): boolean {
  if (pathname === '/admin/marketing') return true; // discount codes; /admin/marketing/qr-codes stays
  return SHOP_ADMIN_ROUTES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export function isShopStorefrontPath(internalPath: string): boolean {
  return SHOP_STOREFRONT_ROUTES.some((p) => internalPath === p || internalPath.startsWith(p + '/'));
}

/**
 * Builder blocks that need the shop: they render nothing on a website's storefront, show a
 * "Wymaga modułu Sklep" notice in the editor, and are left out of the block library.
 * Not 'auth-box': /logowanie renders it and the site's own staff sign in there (it hides
 * customer registration by itself).
 */
export const SHOP_BLOCK_TYPES = new Set<string>([
  'products', 'categories', 'cart', 'checkout', 'catalog-filters', 'subcategories', 'category-header',
  'product-showroom', 'pick-pack', 'store-search',
  'account-profile', 'account-orders', 'account-addresses', 'club-benefits', 'club-promo',
  'product-name', 'product-price', 'product-quantity', 'product-gallery', 'product-description-short',
  'product-description-long', 'product-feature-bullets', 'product-variants', 'product-add-to-cart',
  'product-stock', 'product-reviews', 'product-rating-mini', 'product-condition', 'product-spec',
  'product-downloads', 'product-leasenow-button', 'product-leasenow-calculator',
  'ticket-name', 'ticket-gallery', 'ticket-description', 'ticket-short-description', 'ticket-price-range',
  'ticket-event-selector', 'ticket-seat-map',
  'stay-search', 'stay-rooms',
]);

export function isShopBlock(type: string | undefined | null): boolean {
  return !!type && SHOP_BLOCK_TYPES.has(type);
}
